<script setup>
import { computed, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/solid'
import {
  Avatar,
  TextButton,
  Divider,
  FormSearchInput,
  Icon,
  Stack,
  Tooltip,
  Typography,
  Tag,
  Box,
} from '@aryeo/runway'

import { useAuth } from '@/Hooks/Customer/useAuth.js'
import CustomerTeamUpdateDefaultMembershipModal from '@/Components/Customer/CustomerTeams/CustomerTeamUpdateDefaultMembershipModal.vue'

defineProps({
  darkMode: { type: Boolean, default: true },
})

const page = usePage()

const { authCustomerTeamMemberships, authCustomerTeamMembership } = useAuth()

const selectDefaultMembershipModalIsOpen = ref(false)
const selectDefaultMembershipIsLoading = ref(false)
const searchTerm = ref('')

const filteredCustomerTeamMemberships = computed(() =>
  authCustomerTeamMemberships.value.data.filter((membership) =>
    membership.customer_team.name.toLowerCase().includes(searchTerm.value.toLowerCase())
  )
)

const showChangeDefault = computed(() => filteredCustomerTeamMemberships.value.length > 1)

const animateTeamSwitcherTooltip = ref(true)
const showTeamSwitcherTooltip = ref(page.props.newCustomerTeam ?? false)

setTimeout(() => {
  animateTeamSwitcherTooltip.value = false
}, 5000)

const isSelected = (membership) => {
  return authCustomerTeamMembership.value.id === membership.id
}

const handleSearch = ({ key, value }) => {
  searchTerm.value = value
}

const handleSelectCustomerTeamMembership = async (membershipId) => {
  router.flushAll()
  router.put(
    route('web.customer.customer-team-memberships.selected.update', membershipId),
    {},
    {
      preserveState: false,
      onSuccess: () => {
        searchTerm.value = ''
      },
    }
  )
}

const handleSelectDefaultCustomerTeamMembership = (membershipId) => {
  selectDefaultMembershipIsLoading.value = true

  router.put(
    route('web.customer.customer-team-memberships.default.update', membershipId),
    {},
    {
      onSuccess: () => {
        selectDefaultMembershipModalIsOpen.value = false
        selectDefaultMembershipIsLoading.value = false
      },
    }
  )
}

const listingsCountForMembership = (membership) =>
  membership.role === 'admin'
    ? listingsCount(membership.customer_team.listings_count)
    : listingsCount(membership.listings_count)

const listingsCount = (count) => {
  if (count > 1) {
    return `${count.toLocaleString()} listings`
  } else if (count === 1) {
    return '1 listing'
  } else {
    return 'No listings'
  }
}

const canCreateTeamsLegend = computed(() => {
  return page.props?.auth.customer_team.company.customers_can_create_teams
    ? 'Create or join a team'
    : 'Join Team'
})
</script>

<template>
  <Menu as="div" class="relative inline-block w-full my-auto text-left">
    <Typography variant="body-small-bold">Current Team</Typography>

    <div>
      <MenuButton
        id="customer-team-menu-button"
        class="flex items-center justify-between w-full h-14 px-3 text-sm rounded-xl focus-visible:shadow-focus"
        :class="
          darkMode
            ? 'text-header-font lg:hover:bg-header-dark'
            : 'text-header-dark hover:text-header-darkest hover:bg-header-off-white border border-border-light'
        "
        @click="showTeamSwitcherTooltip = false"
      >
        <div class="text-left truncate">
          <Typography variant="body-small" as="p">
            <span class="sr-only">Open customer team menu</span>
            {{ authCustomerTeamMembership.customer_team.name }}
          </Typography>
          <Typography variant="body-xs" color="subtle" as="p">
            {{ `Team ${authCustomerTeamMembership.role}` }}
          </Typography>
        </div>
        <ChevronDownIcon class="block w-5 h-5 ml-1 shrink-0" aria-hidden="true" />

        <transition
          leave-active-class="transition duration-150"
          leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-90 opacity-0"
        >
          <Tooltip
            v-if="showTeamSwitcherTooltip"
            placement="bottom"
            :contentInteractive="true"
            trigger="mouseenter focus"
            class="absolute -right-1"
          >
            <template #default>
              <div @mouseenter="animateTeamSwitcherTooltip = false" class="relative flex size-4">
                <Icon name="infoCircle" size="small" color="success" />
                <span
                  v-if="animateTeamSwitcherTooltip"
                  class="hidden motion-safe:inline-flex animate-ping absolute h-full w-full rounded-full bg-emerald-400 opacity-60"
                ></span>
              </div>
            </template>
            <template #content>
              <Stack class="p-2" space="xsmall">
                <Typography as="p"> Switch between team spaces here. </Typography>
              </Stack>
            </template>
          </Tooltip>
        </transition>
      </MenuButton>
    </div>

    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="scale-95 opacity-0"
      enter-to-class="scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="scale-100 opacity-100"
      leave-to-class="scale-95 opacity-0"
    >
      <MenuItems
        class="absolute max-h-screen-75 overflow-y-auto left-0 z-30 w-full mt-1 px-2 py-3 origin-top-left bg-white rounded-xl shadow-card"
      >
        <MenuItem v-if="authCustomerTeamMemberships.data.length >= 10">
          <FormSearchInput
            :value="searchTerm"
            hasNoResultsMessage="No customer teams were found for your search."
            placeholderText="Search by team name..."
            @update:modelValue="handleSearch"
          />
        </MenuItem>
        <MenuItem
          v-for="customerTeamMembership in filteredCustomerTeamMemberships"
          :key="customerTeamMembership.id"
          class="rounded-xl cursor-pointer"
          :class="[
            isSelected(customerTeamMembership)
              ? 'bg-theme-50 border-primary border-[3px]'
              : 'hover:bg-theme-50 border-border-light border-[2px]',
            'mb-3',
          ]"
          @click.prevent="handleSelectCustomerTeamMembership(customerTeamMembership)"
        >
          <div class="group text-sm">
            <Stack space="xxsmall">
              <Tooltip
                v-if="customerTeamMembership.customer_team.is_archived"
                placement="right"
                text="This team has been archived. You can still access your orders, but to place additional orders and manage future listings, please select another team in this menu or create a new one."
              >
                <Box
                  borderWidth="xsmall"
                  borderRadius="xlarge"
                  borderColor="border-light"
                  margin="xxsmall"
                  padding="xxsmall"
                  paddingX="small"
                  backgroundColor="background"
                >
                  <div class="flex flex-row items-center justify-between">
                    <Typography variant="label-small" color="subtle">Archived Team</Typography>
                    <Icon name="infoSquare" size="small" color="subtle" />
                  </div>
                </Box>
              </Tooltip>
              <Tooltip
                v-else-if="customerTeamMembership.is_archived"
                placement="right"
                text="You are an archived member of this team. You can still access your orders, but to place additional orders and manage future listings, please select another team in this menu or create a new one."
              >
                <Box
                  borderWidth="xsmall"
                  borderRadius="xlarge"
                  borderColor="border-light"
                  margin="xxsmall"
                  padding="xxsmall"
                  paddingX="small"
                  backgroundColor="background"
                >
                  <div class="flex flex-row items-center justify-between">
                    <Typography variant="label-small" color="subtle">Archived Member</Typography>
                    <Icon name="infoSquare" size="small" color="subtle" />
                  </div>
                </Box>
              </Tooltip>

              <Tooltip
                v-else-if="!customerTeamMembership.invitation_accepted_at"
                placement="right"
                text="You've been invited to this team. Click to accept or decline your invite."
              >
                <Box
                  borderWidth="xsmall"
                  borderRadius="xlarge"
                  borderColor="border-light"
                  margin="xxsmall"
                  padding="xxsmall"
                  paddingX="small"
                  backgroundColor="background"
                >
                  <div class="flex flex-row items-center justify-between">
                    <Typography variant="label-small" color="subtle">Invitation Pending</Typography>
                    <Icon name="infoSquare" size="small" color="subtle" />
                  </div>
                </Box>
              </Tooltip>

              <div class="flex flex-row justify-between w-full p-3 cursor-pointer">
                <Stack space="xsmall">
                  <div class="inline-flex" v-if="customerTeamMembership.is_default">
                    <Tag color="info" label="Default" isSmall />
                  </div>
                  <Stack space="none">
                    <Typography variant="body-xs-bold" as="p" color="subtle" text-wrap>
                      {{ customerTeamMembership.customer_team.name }}
                    </Typography>
                    <Typography variant="fineprint" as="p" color="subtle">
                      {{ `Team ${customerTeamMembership.role}` }}
                    </Typography>
                    <Typography variant="fineprint" as="p" color="subtle">
                      {{ listingsCountForMembership(customerTeamMembership) }}
                    </Typography>
                  </Stack>
                </Stack>
                <Avatar
                  class="shrink-0 ml-2"
                  :image="customerTeamMembership.customer_team.logo_url"
                  :firstName="customerTeamMembership.customer_team.name"
                  size="small"
                />
              </div>
            </Stack>
          </div>
        </MenuItem>
        <Divider class="my-3" />
        <div class="group flex flex-col text-sm">
          <TextButton
            :href="route('web.customer.customer-teams.create-or-join')"
            :text="canCreateTeamsLegend"
          />
          <button
            v-if="showChangeDefault"
            class="text-gray-500 text-left p-3 hover:bg-gray-100 rounded-b-md"
            @click.prevent="selectDefaultMembershipModalIsOpen = true"
          >
            Change default
          </button>
        </div>
      </MenuItems>
    </transition>
  </Menu>

  <CustomerTeamUpdateDefaultMembershipModal
    v-if="showChangeDefault"
    :customerTeamMemberships="authCustomerTeamMemberships.data"
    :isOpen="selectDefaultMembershipModalIsOpen"
    @onClose="selectDefaultMembershipModalIsOpen = false"
    @onConfirm="handleSelectDefaultCustomerTeamMembership"
  />
</template>
